import { Icon, useBreakpointValue } from '@chakra-ui/react';
import findLast from 'lodash/findLast';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { MdClose, MdDragIndicator, MdOutlineEdit } from 'react-icons/md';
import { useAtom } from 'jotai';
import { CHAT_PANEL_MIN_WIDTH } from '../../constants/chat';
import { useChatSocket } from '../../services/chatSocket';
import { chatPanelOpenState } from '../../store/global.store';
import { MButton, MText } from '../Monetize';
import { MBox, MFlex, MIconButton, MTooltip } from '../Monetize';
import { getLiveActivityLabel } from './activityLabels';
import { ChatContainer } from './ChatContainer';
import { ChatStatus } from './ChatStatus';
import { MontyAvatar } from './MontyAvatar';
import { useChatPanelResize } from './useChatPanelResize';

export const ChatPanel: FC = () => {
  const [isOpen, setIsOpen] = useAtom(chatPanelOpenState);
  const {
    reset,
    isRestoringSession,
    messages,
    isThinking,
    isLoading,
    isReconnecting,
  } = useChatSocket();
  const hasConversation = messages.some(({ role }) => role === 'user');
  const latestActivity = useMemo(
    () => findLast(messages, ({ role }) => role === 'user')?.activity,
    [messages],
  );
  const activityLabel =
    isLoading && latestActivity
      ? getLiveActivityLabel(latestActivity)
      : undefined;
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
  const {
    width,
    maxWidth,
    isResizing,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onKeyDown,
    onDoubleClick,
  } = useChatPanelResize();

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {isMobile && (
        <MBox
          position="fixed"
          inset={0}
          bg="blackAlpha.500"
          zIndex="overlay"
          onClick={handleClose}
        />
      )}
      <MFlex
        role="dialog"
        aria-label="Monty AI assistant"
        aria-modal={isMobile}
        direction="column"
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        width={isMobile ? '100%' : `${width}px`}
        maxW="100vw"
        bg="bg.surface"
        borderLeft="1px solid"
        borderColor="border.subtle"
        boxShadow="-8px 0 24px rgba(0, 0, 0, 0.08)"
        zIndex="modal"
      >
        {!isMobile && (
          <MBox
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat panel"
            aria-valuemin={CHAT_PANEL_MIN_WIDTH}
            aria-valuemax={maxWidth}
            aria-valuenow={Math.round(width)}
            tabIndex={0}
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            width="6px"
            ml="-3px"
            cursor="col-resize"
            zIndex={1}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onKeyDown={onKeyDown}
            onDoubleClick={onDoubleClick}
            className="group"
            // The 6px hit area straddles the panel's 1px left border and
            // paints nothing itself; the grip below shows hover, drag and
            // focus, so the edge never gets a second border.
            _focusVisible={{ outline: 'none' }}
          >
            {/* Visible grip so the edge can be grabbed without hunting for
                it; centred on the border pixel (2.5px into the hit area). */}
            <MFlex
              aria-hidden
              position="absolute"
              top="50%"
              left="2.5px"
              transform="translate(-50%, -50%)"
              alignItems="center"
              justifyContent="center"
              w="14px"
              h={8}
              borderRadius="full"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={isResizing ? 'tIndigo.base' : 'border.subtle'}
              bg="bg.surface"
              color={isResizing ? 'tIndigo.base' : 'fg.muted'}
              cursor={isResizing ? 'grabbing' : 'grab'}
              boxShadow="xs"
              transition="color 0.15s, border-color 0.15s"
              _groupHover={{
                color: 'tIndigo.base',
                borderColor: 'tIndigo.base',
              }}
              _groupFocusVisible={{
                color: 'tIndigo.base',
                borderColor: 'tIndigo.base',
              }}
            >
              <Icon as={MdDragIndicator} boxSize={3.5} />
            </MFlex>
          </MBox>
        )}
        <MFlex
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          py={3}
          px={4}
          borderBottom="1px solid"
          borderColor="border.subtle"
          bgGradient="linear-gradient(180deg, {colors.tMagenta.50} 0%, {colors.tWhite.base} 100%)"
        >
          <MFlex alignItems="center" gap={2.5} minW={0}>
            <MontyAvatar boxSize={8} />
            <MBox minW={0}>
              <MText
                fontSize="md"
                fontWeight={600}
                color="fg.strong"
                lineHeight="1.2"
              >
                Monty
              </MText>
              <ChatStatus
                isThinking={isThinking}
                isLoading={isLoading}
                isReconnecting={isReconnecting}
                activityLabel={activityLabel}
              />
            </MBox>
          </MFlex>
          <MFlex alignItems="center" gap={1} flexShrink={0}>
            {hasConversation && (
              <MTooltip label="Start a new chat" placement="bottom" hasArrow>
                <MButton
                  size="sm"
                  variant="ghost"
                  color="fg.muted"
                  _hover={{ bg: 'bg.hover', color: 'fg.strong' }}
                  leftIcon={<Icon as={MdOutlineEdit} boxSize={3.5} />}
                  onClick={reset}
                  isDisabled={isRestoringSession}
                >
                  New Chat
                </MButton>
              </MTooltip>
            )}
            <MTooltip label="Close" placement="bottom" hasArrow>
              <MIconButton
                aria-label="Close chat"
                icon={<Icon as={MdClose} boxSize={4} />}
                size="sm"
                variant="ghost"
                color="fg.muted"
                _hover={{ bg: 'bg.hover', color: 'fg.strong' }}
                onClick={handleClose}
              />
            </MTooltip>
          </MFlex>
        </MFlex>
        {/* `isolation` keeps ChatContainer's z-indexed layers (message list,
            input) below the resize grip, which reaches into the panel. */}
        <MBox flex={1} bg="bg.canvas" overflow="hidden" isolation="isolate">
          <ChatContainer />
        </MBox>
      </MFlex>
    </>
  );
};
